import React, { useState, useEffect } from "react";
import { Edit3, Save, X, Lock } from "lucide-react";
import { motion } from "framer-motion";
import { useParams } from "react-router-dom";
import {
  getAssessmentPlan,
  saveAssessmentPlan,
} from "../../../../services/course.service";
import { useUtilityContext } from "../../../../context/UtilityContext";
import toast from "react-hot-toast";
import {
  card,
  cardTitle,
  microLabel,
  bodySecondary,
  dividerInner,
  iconSize,
} from "../../../../lib/design-system";
import { cn } from "../../../../lib/utils";
import { Skeleton } from "../../../StudentDashboard/Components/ui/Skeleton";

// Color palette for the 3 categories
const COLORS = [
  { bg: "bg-accent1", text: "text-accent1", light: "bg-accent1/10 dark:bg-accent1/20" },
  { bg: "bg-emerald-500", text: "text-emerald-600 dark:text-emerald-400", light: "bg-emerald-50 dark:bg-emerald-900/30" },
  { bg: "bg-amber-500", text: "text-amber-600 dark:text-amber-400", light: "bg-amber-50 dark:bg-amber-900/30" },
];

const AssessmentPlan = () => {
  const { courseID: urlCourseID } = useParams();
  const { selectedCourseId } = useUtilityContext();
  const courseID = urlCourseID || selectedCourseId;

  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [assessmentPlan, setAssessmentPlan] = useState({
    endTermExam: 50,
    midTermExam: 30,
    continuousAssessment: 20,
  });
  const [tempPlan, setTempPlan] = useState(assessmentPlan);
  const [isLocked, setIsLocked] = useState(false);

  useEffect(() => {
    const fetchAssessmentPlan = async () => {
      if (!courseID) return;
      try {
        setLoading(true);
        const data = await getAssessmentPlan(courseID);
        if (data) {
          const plan = {
            endTermExam: data.endTermExam,
            midTermExam: data.midTermExam,
            continuousAssessment: data.continuousAssessment,
          };
          setAssessmentPlan(plan);
          setTempPlan(plan);
          setIsLocked(data.isLocked || false);
        } else {
          const defaultPlan = { endTermExam: 50, midTermExam: 30, continuousAssessment: 20 };
          setAssessmentPlan(defaultPlan);
          setTempPlan(defaultPlan);
        }
      } catch (error) {
        console.error("Error fetching assessment plan:", error);
        toast.error("Failed to load assessment plan");
      } finally {
        setLoading(false);
      }
    };
    fetchAssessmentPlan();
  }, [courseID]);

  const handleEdit = () => {
    if (isLocked) {
      toast.error("Assessment plan is locked. Grades have been entered.");
      return;
    }
    setTempPlan({ ...assessmentPlan });
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (isLocked) {
      toast.error("Assessment plan is locked. Grades have been entered.");
      return;
    }
    const total = tempPlan.endTermExam + tempPlan.midTermExam + tempPlan.continuousAssessment;
    if (total !== 100) {
      toast.error(`Total must equal 100. Currently ${total}.`);
      return;
    }
    try {
      await saveAssessmentPlan(courseID, tempPlan);
      setAssessmentPlan(tempPlan);
      setIsEditing(false);
      toast.success("Assessment plan saved successfully");
    } catch (error) {
      console.error("Error saving assessment plan:", error);
      toast.error(error.response?.data?.error || "Failed to save assessment plan");
    }
  };

  const handleCancel = () => {
    setTempPlan({ ...assessmentPlan });
    setIsEditing(false);
  };

  const handleChange = (field, value) => {
    setTempPlan((prev) => ({ ...prev, [field]: parseInt(value) || 0 }));
  };

  const plan = isEditing ? tempPlan : assessmentPlan;
  const totalMarks = plan.endTermExam + plan.midTermExam + plan.continuousAssessment;

  const categories = [
    { key: "endTermExam", label: "End-Term", shortLabel: "ET" },
    { key: "midTermExam", label: "Mid-Term", shortLabel: "MT" },
    { key: "continuousAssessment", label: "Continuous", shortLabel: "CA" },
  ];

  if (loading) {
    return (
      <div className={cn(card, "overflow-hidden")}>
        <div className={cn("px-5 py-4 border-b", dividerInner)}>
          <div className="flex items-center gap-2.5">
            <Skeleton className="h-5 w-5" />
            <Skeleton className="h-5 w-32" />
          </div>
        </div>
        <div className="p-5 space-y-4">
          <Skeleton className="h-6 w-full rounded-sm" />
          <div className="grid grid-cols-3 gap-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(card, "overflow-hidden")}>
      {/* Header */}
      <div className={cn("px-5 py-4 border-b flex items-center justify-between", dividerInner)}>
        <div className="flex items-center gap-2.5">
          <h3 className={cardTitle}>Assessment Plan</h3>
          {isLocked && (
            <Lock className="h-3.5 w-3.5 text-amber-500 dark:text-amber-400 ml-1" />
          )}
        </div>

        {!isEditing && !isLocked && (
          <button
            onClick={handleEdit}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-accent1/10 text-accent1 text-xs font-bold uppercase tracking-wider hover:bg-accent1/20 transition-colors"
          >
            <Edit3 className={iconSize.sm} />
            Edit
          </button>
        )}
        {isEditing && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleCancel}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-bold uppercase tracking-wider hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              <X className={iconSize.sm} />
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm bg-accent1 text-white text-xs font-bold uppercase tracking-wider hover:bg-accent1/90 transition-colors"
            >
              <Save className={iconSize.sm} />
              Save
            </button>
          </div>
        )}
      </div>

      <div className="p-5 space-y-5">
        {/* Stacked bar visualization */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className={microLabel}>Distribution</span>
            <span
              className={cn(
                "text-xs font-black tracking-tight",
                totalMarks === 100
                  ? "text-accent1"
                  : "text-red-500 dark:text-red-400"
              )}
            >
              {totalMarks}/100
            </span>
          </div>

          <div className="flex h-5 rounded-sm overflow-hidden bg-gray-100 dark:bg-gray-700">
            {categories.map((cat, i) => {
              const value = plan[cat.key];
              const pct = totalMarks > 0 ? (value / totalMarks) * 100 : 0;
              return (
                <motion.div
                  key={cat.key}
                  className={cn(COLORS[i].bg, "relative flex items-center justify-center")}
                  initial={{ width: 0 }}
                  animate={{ width: `${pct}%` }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                >
                  {pct > 12 && (
                    <span className="text-[10px] font-bold text-white/90 tabular-nums">
                      {value}%
                    </span>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Category cards */}
        <div className="grid grid-cols-3 gap-3">
          {categories.map((cat, i) => (
            <div
              key={cat.key}
              className={cn(
                "rounded-sm p-3 text-center border",
                "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900"
              )}
            >
              {/* Color dot + label */}
              <div className="flex items-center justify-center gap-1.5 mb-2">
                <div className={cn("w-2 h-2 rounded-sm", COLORS[i].bg)} />
                <span className={cn(microLabel, "text-[10px]")}>{cat.label}</span>
              </div>

              {isEditing && !isLocked ? (
                <input
                  type="number"
                  value={tempPlan[cat.key]}
                  onChange={(e) => handleChange(cat.key, e.target.value)}
                  className={cn(
                    "w-full text-xl font-black tracking-tight text-center bg-transparent border-none outline-none",
                    COLORS[i].text
                  )}
                  min="0"
                  max="100"
                />
              ) : (
                <div className={cn("text-xl font-black tracking-tight", COLORS[i].text)}>
                  {assessmentPlan[cat.key]}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Validation */}
        {isEditing && !isLocked && totalMarks !== 100 && (
          <div
            className={cn(
              "px-3 py-2.5 rounded-sm text-xs font-medium border",
              totalMarks < 100
                ? "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-700"
                : "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-700"
            )}
          >
            {totalMarks < 100
              ? `Add ${100 - totalMarks} more marks to reach 100.`
              : `Reduce by ${totalMarks - 100} marks. Total exceeds 100.`}
          </div>
        )}

        {isEditing && !isLocked && totalMarks === 100 && (
          <div className="px-3 py-2.5 rounded-sm text-xs font-medium bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700">
            Total marks equal 100.
          </div>
        )}
      </div>
    </div>
  );
};

export default AssessmentPlan;
